import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import axios, { AxiosResponse } from "axios";

import { getCompanyContactBook } from "api";
import { Contact } from "api/models";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import FormField from "components/molecules/FormField/FormField";
import FormItemInfo from "../../../Common/FormItemInfo";
import SearchSelect from "../../../Common/SearchSelect";
import { handleContactRenderOption } from "../../PickupDeliveryAddress/helpers";

interface StopContactFormSectionProps {
  index: number;
  contactBook?: any[];
}

function mapContactsToOptions(contacts: Contact[] = []) {
  return contacts.map((contact) => ({
    id: contact.id,
    label: contact.name,
    value: contact.name,
    company_name: contact.company_name,
    phone_number: contact.phone_number,
    email: contact.email,
  }));
}

export default function StopContactFormSection({ index, contactBook }: StopContactFormSectionProps) {
  const intl = useIntl();
  const { setValue, getValues, control } = useFormContext();
  const [contactSearchResults, setContactSearchResults] = useState<Contact[] | null>(null);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const contactName = useWatch({ name: `schedule.stops.${index}.stop_contact_name`, control });
  const isServerSearch = (contactName?.length || 0) >= 3;

  useEffect(() => {
    if (!isServerSearch) {
      setContactSearchResults(null);
      setIsLoadingContacts(false);
      return undefined;
    }

    const location = getValues("organizational.location") || getValues("shipment.location");
    if (!location) {
      return undefined;
    }

    const controller = new AbortController();
    setIsLoadingContacts(true);

    getCompanyContactBook({
      params: {
        location,
        query: contactName,
      },
      signal: controller.signal,
    })
      .then((res: AxiosResponse) => {
        setContactSearchResults(res.data.results || []);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          errorToast(
            err,
            intl.formatMessage({
              id: "new-request.schedule.couldNotRetrieveContactBook",
              defaultMessage: "Could not retrieve contact book. Please try again later.",
            }),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingContacts(false);
        }
      });

    return () => controller.abort();
  }, [contactName, isServerSearch, getValues, intl]);

  const effectiveContactBook =
    isServerSearch && contactSearchResults !== null ? contactSearchResults : contactBook || [];

  const filterContactName = (option: any, itemProps: any, filterKey = "label") =>
    option[filterKey].toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1 ||
    option.company_name?.toLowerCase?.().indexOf(itemProps.text.toLowerCase()) !== -1;

  const autoFillContact = useCallback(
    (event) => {
      const contact = event[0];
      if (contact) {
        setValue(`schedule.stops.${index}.stop_contact_name`, contact.label, { shouldValidate: true });
        setValue(`schedule.stops.${index}.stop_contact_number`, contact.phone_number, { shouldValidate: true });
        setValue(`schedule.stops.${index}.stop_contact_email`, contact.email, { shouldValidate: true });
      }
    },
    [index, setValue],
  );

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <div className="mr-xl" style={{ minWidth: "200px" }}>
        <FormItemInfo labelId="general.contact" label="Contact" />
      </div>
      <div>
        <div className="flex-row flex-wrap main-wrapper">
          <div className="w-192">
            <SearchSelect
              name={`schedule.stops.${index}.stop_contact_name`}
              testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_CONTACT_NAME_PREFIX}-${index}`}
              enableManualValue
              label={intl.formatMessage({
                id: "general.name",
                defaultMessage: "Name",
              })}
              preselect={false}
              options={mapContactsToOptions(effectiveContactBook)}
              placeholder={intl.formatMessage({
                id: "new-request.schedule.chooseContactPerson",
                defaultMessage: "Search for a contact person",
              })}
              filterBy={isServerSearch ? () => true : filterContactName}
              isLoading={isLoadingContacts}
              handleRenderOption={handleContactRenderOption}
              onSelect={autoFillContact}
            />
          </div>

          <div className="w-192">
            <FormField
              id="id_pickup_contact_phone"
              label={intl.formatMessage({
                id: "new-request.schedule.pickupContactPhone",
                defaultMessage: "Phone",
              })}
              type="textinput"
              field_id={`schedule.stops.${index}.stop_contact_number`}
              placeholder={intl.formatMessage({
                id: "new-request.schedule.phoneInfo",
                defaultMessage: "Type phone number here...",
              })}
              onChange={(e) => {
                setValue(`schedule.stops.${index}.stop_contact_number`, e.target.value);
              }}
              testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_CONTACT_PHONE_PREFIX}-${index}`}
            />
          </div>
          <div className="w-192">
            <FormField
              id="id_pickup_contact_email"
              label={intl.formatMessage({
                id: "new-request.schedule.pickupContactEmail",
                defaultMessage: "Email",
              })}
              placeholder={intl.formatMessage({
                id: "new-request.schedule.emailInfo",
                defaultMessage: "Type email here...",
              })}
              type="textinput"
              field_id={`schedule.stops.${index}.stop_contact_email`}
              onChange={(e) => {
                setValue(`schedule.stops.${index}.stop_contact_email`, e.target.value);
              }}
              testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_CONTACT_EMAIL_PREFIX}-${index}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

StopContactFormSection.defaultProps = {
  contactBook: [],
};
